import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

/**
 * `penn_config.sherex_options` admin surface. Route key is `optionId`
 * (`sherex_options.option_id`, a natural key that may contain spaces/slashes
 * — always encodeURIComponent'd below).
 *
 * IMPORTANT: there is NO foreign key to `penn_config.sherex_pricing`. Many
 * options share ONE price row (e.g. all 34 Flex-5 variants share "FLEX5").
 * `priceGroupKey` + `priceSharedWithCount` tell you which group this option's
 * price belongs to and how many siblings share it — saving a price here
 * changes it for all of them, not just this row.
 *
 * Both currencies live on that SAME shared row — the EU and US admin pages
 * mount the same page component with a `currency` prop (like Autoriv
 * Configurator Items) and each writes ONLY its own column.
 *
 * They list the SAME options: the admin pages no longer send `region`, so both
 * mounts get the full catalog. `region` (below) is still stored per row and set
 * from the page it was added on — it just no longer filters the list or scopes
 * the by-id routes. The API still supports the filter for other callers.
 */

/**
 * Which regional catalog an option belongs to — a STORED column on
 * `sherex_options`, set from the page the row is added on (EU page -> EU, NA
 * page -> NA) and immutable after. Read it off the row; the create body sends
 * the page's own region and never derives one (§L — the backend owns the write).
 */
export type SherexOptionRegion = "EU" | "NA";

export interface SherexOption {
  optionId: string;
  productGroup: string | null;
  threadSize: string | null;
  installationMethod: string | null;
  pullingForce: string | null;
  availableStroke: string | null;
  airPressure: string | null;
  airUse: string | null;
  toolSpeed: string | null;
  weight: string | null;
  completeHeadSetAssemblyPn: string | null;
  mandrelPn: string | null;
  anvilPn: string | null;
  completeSetUps: string | null;
  includes: string | null;
  /** Decimal string, numeric(12,4). Resolved via the shared pricing key. */
  priceEur: string | null;
  /** Decimal string, numeric(12,4). Resolved via the shared pricing key. */
  priceUsd: string | null;
  /** The `sherex_pricing.product_name` this option's price reads/writes. */
  priceGroupKey: string;
  /** Count of OTHER active options sharing `priceGroupKey`. */
  priceSharedWithCount: number;
  /** Stored regional catalog — which of the two pages this row belongs to. */
  region: SherexOptionRegion;
  createdAt: string;
  updatedAt: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedSherexOptions {
  data: SherexOption[];
  pagination: PaginationMeta;
}

/**
 * The ID / Part No format one product group accepts.
 *
 * There is no FK from `sherex_options` to `sherex_pricing`, so a mistyped id
 * doesn't fail — it opens a pricing product nothing prices. Each group publishes
 * the stems its ids must start with; a valid id is one of them followed by the
 * (free-text) part number, e.g. `SSG-801-` + `04-1440`.
 *
 * The RULE lives in svc-04 and is enforced there on create/update (400
 * `sherex-option-id-pattern-mismatch`). It arrives here only so the Add modal
 * can apply it before Save — never as an independent client-side rule (§L).
 */
export interface SherexIdPattern {
  /** The product group this rule applies to, spelled as stored. */
  productGroup: string;
  /** Literal stems a valid id may start with, compared case-insensitively. */
  prefixes: string[];
  /** Human-readable rule, rendered verbatim as the field hint. */
  description: string;
  /** A valid example id for that hint. */
  example: string;
}

export interface SherexOptionGroups {
  groups: string[];
  /**
   * Id rule per group, for the groups that declare one. A group missing here has
   * no declared format and keeps a free-text id.
   */
  idPatterns: SherexIdPattern[];
}

export interface SherexPricePreview {
  priceGroupKey: string;
  priceEur: string | null;
  priceUsd: string | null;
  sharedWithCount: number;
}

/**
 * One pricing product inside a product group. A group is NOT a pricing group:
 * `SSG-800` spans SSG-801/802/803/808/813 because the key is read out of the
 * option id, so the Add modal makes the admin pick the product and then locks
 * `idPrefix` in front of the ID input to guarantee the id resolves there.
 */
export interface SherexPriceProduct {
  /** Resolved `sherex_pricing.product_name`. */
  productName: string;
  /** Id prefix that resolves to `productName` (e.g. "SSG-801", "FLEX-5"). */
  idPrefix: string;
  /**
   * The group's published stems that land on this product, separator included
   * — `["SSG-801-"]`, or `["FLEX-5 ", "FLEX-5P ", "FLEX-5S "]` for FLEX5.
   *
   * These are the stems svc-04 ACCEPTS on create, paired with the pricing row
   * they reach, so the Add modal can lock one in front of the part number and
   * know the composed id passes both checks. Empty for a group with no
   * declared format — the modal falls back to `idPrefix`.
   */
  idPrefixes: string[];
  /** Active options already in this pricing product. */
  optionCount: number;
  priceEur: string | null;
  priceUsd: string | null;
}

export interface SherexPriceProducts {
  productGroup: string;
  /**
   * Id prefix shared by every product in the group — what the Add modal locks
   * in front of the ID input. `SSG-800` -> `SSG-8`, so typing `01-0440` lands
   * in pricing product SSG-801 and `17-0440` opens a new SSG-817.
   */
  idPrefix: string;
  products: SherexPriceProduct[];
}

export type SherexOptionSortColumn =
  | "optionId"
  | "productGroup"
  | "threadSize"
  | "installationMethod"
  | "pullingForce"
  | "toolSpeed"
  | "weight"
  | "priceEur";

export interface ListSherexOptionsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: SherexOptionSortColumn;
  sortOrder?: "asc" | "desc";
  /**
   * Restrict the page to one regional catalog. Filtered server-side before
   * paging, so `pagination.total` is that region's count. Omit for all rows.
   */
  region?: SherexOptionRegion;
}

export interface CreateSherexOptionBody {
  optionId: string;
  productGroup: string;
  /** Catalog this option is filed under — the page sends its own mount's
   *  region. Required and stored as-is (the page wins over the thread). */
  region: SherexOptionRegion;
  threadSize?: string | null;
  installationMethod?: string | null;
  pullingForce?: string | null;
  availableStroke?: string | null;
  airPressure?: string | null;
  airUse?: string | null;
  toolSpeed?: string | null;
  weight?: string | null;
  completeHeadSetAssemblyPn?: string | null;
  mandrelPn?: string | null;
  anvilPn?: string | null;
  completeSetUps?: string | null;
  includes?: string | null;
  /** The API requires AT LEAST ONE of priceEur / priceUsd. Each currency page
   *  sends only its own column, leaving the sibling for the other page. */
  priceEur?: string | number | null;
  priceUsd?: string | number | null;
}

/** PATCH — send only changed fields. `optionId` (the PK) is never patchable,
 *  and `region` is immutable (set once from the page it was added on). */
export type UpdateSherexOptionBody = Partial<
  Omit<CreateSherexOptionBody, "optionId" | "region">
>;

const BASE = "/v1/sherex-options";

export async function listSherexOptions(
  params: ListSherexOptionsParams = {},
): Promise<PaginatedSherexOptions> {
  const res = await Api.get(BASE, { params });
  return unwrap<PaginatedSherexOptions>(res.data);
}

/**
 * `region` scopes the lookup to the calling page's catalog. `optionId` is a
 * global PK, so without it the EU page would happily load an NA row into its
 * view/edit modal; with it the backend 404s instead.
 */
export async function getSherexOption(
  optionId: string,
  region?: SherexOptionRegion,
): Promise<SherexOption> {
  const res = await Api.get(`${BASE}/${encodeURIComponent(optionId)}`, {
    params: { region },
  });
  return unwrap<SherexOption>(res.data);
}

export async function listSherexOptionGroups(): Promise<SherexOptionGroups> {
  const res = await Api.get(`${BASE}/groups`);
  return unwrap<SherexOptionGroups>(res.data);
}

export async function getSherexPricePreview(
  optionId: string,
  productGroup: string,
): Promise<SherexPricePreview> {
  const res = await Api.get(`${BASE}/price-preview`, {
    params: { optionId, productGroup },
  });
  return unwrap<SherexPricePreview>(res.data);
}

export async function listSherexPriceProducts(
  productGroup: string,
): Promise<SherexPriceProducts> {
  const res = await Api.get(`${BASE}/price-products`, {
    params: { productGroup },
  });
  return unwrap<SherexPriceProducts>(res.data);
}

export async function createSherexOption(
  body: CreateSherexOptionBody,
): Promise<SherexOption> {
  const res = await Api.post(BASE, body);
  return unwrap<SherexOption>(res.data);
}

/**
 * `region` travels as a QUERY param, not in the body — the body is the patch,
 * and region must never look patchable (it is immutable once set). Server-side
 * it only narrows the WHERE, so a cross-catalog id 404s.
 */
export async function updateSherexOption(
  optionId: string,
  body: UpdateSherexOptionBody,
  region?: SherexOptionRegion,
): Promise<SherexOption> {
  const res = await Api.patch(`${BASE}/${encodeURIComponent(optionId)}`, body, {
    params: { region },
  });
  return unwrap<SherexOption>(res.data);
}

/** `region`-scoped so a page can only soft-delete rows from its own catalog. */
export async function deleteSherexOption(
  optionId: string,
  region?: SherexOptionRegion,
): Promise<{ success: true }> {
  const res = await Api.delete(`${BASE}/${encodeURIComponent(optionId)}`, {
    params: { region },
  });
  return unwrap<{ success: true }>(res.data);
}
